import { useMemo, useState } from "react";

import ace from "brace";
import "brace/mode/json";
import "brace/theme/github";
import clsx from "clsx";
import { JsonEditor } from "jsoneditor-react";
import { debounce } from "lodash";
import {
  Builder,
  Query,
  type ImmutableTree,
} from "react-awesome-query-builder";
import { twMerge } from "tailwind-merge";

import { useSalesBreakdownConfigs } from "@components/data/sales-breakdown-configs";
import { useUser } from "@components/user-context";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { TertiaryButton } from "@shared/buttons";

import {
  getAdvancedSearchEditorConfig,
  getDefaultRule,
  jsonLogicToSearchTree,
  searchTreeToJsonLogic,
} from "./config";

interface Props {
  value: any;
  labelClassName?: string;
  queryBuilderClassName?: string;
  onChange(value: any): void;
  onSwitchToSimpleSearch(): void;
}

export const AdvancedSearchEditor = ({
  value,
  labelClassName,
  queryBuilderClassName,
  onChange,
  onSwitchToSimpleSearch,
}: Props) => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const { user } = useUser();
  const { value: breakdownConfigs } = useSalesBreakdownConfigs();

  const breakdowns = useMemo(
    () => breakdownConfigs.map((e) => e.key),
    [breakdownConfigs],
  );

  const [matcher, setMatcher] = useState<any>(value ?? getDefaultRule());
  const [tree, setTree] = useState(() =>
    jsonLogicToSearchTree(matcher, breakdowns),
  );
  const [isRaw, setIsRaw] = useState(false);

  const debouncedOnChangeTree = useMemo(
    () =>
      debounce((val: ImmutableTree) => {
        const toSet = searchTreeToJsonLogic(val, breakdowns);
        setMatcher(toSet);
        onChange(toSet);
      }, 500),
    [breakdowns],
  );

  const debouncedOnChangeRaw = useMemo(
    () =>
      debounce((val: any) => {
        const toSet = val || getDefaultRule();
        try {
          setTree(jsonLogicToSearchTree(toSet, breakdowns));
        } catch (e) {
          return;
        }

        setMatcher(toSet);
        onChange(toSet);
      }, 500),
    [breakdowns],
  );

  const renderBuilder = useMemo(
    () => (props: any) =>
      (
        <div className="query-builder-container overflow-x-hidden">
          <div className="query-builder qb-lite m-0 -mr-2.5">
            <Builder {...props} />
          </div>
        </div>
      ),
    [],
  );

  return (
    <div className="mt-4 bg-v2-primary">
      <div className="flex items-center justify-between mb-2">
        <h4 className={twMerge("m-0 text-[11px] font-bold", labelClassName)}>
          ADVANCED SEARCH
        </h4>
        <div className="flex items-center gap-5">
          {user.isAdmin && (
            <>
              {isRaw ? (
                <TertiaryButton
                  className={clsx(
                    "m-0 p-0 leading-3 bg-v2-primary flex items-center gap-1",
                    "group hover:text-cosmic+2",
                  )}
                  onClick={() => {
                    prettyLogEventPage({
                      domain: "Metrics Explorer",
                      action: "onClick",
                      additionalActionInfo:
                        "Change advanced search editor to structured moda",
                    });

                    setIsRaw(false);
                  }}
                >
                  <i className="fa-regular fa-block-quote text-xxs group-hover:text-cosmic+2" />
                  View structured
                </TertiaryButton>
              ) : (
                <TertiaryButton
                  className={clsx(
                    "m-0 p-0 leading-3 bg-v2-primary flex items-center gap-1",
                    "group hover:text-cosmic+2",
                  )}
                  onClick={() => {
                    prettyLogEventPage({
                      domain: "Metrics Explorer",
                      action: "onClick",
                      additionalActionInfo:
                        "Change advanced search editor to raw moda",
                    });

                    setIsRaw(true);
                  }}
                >
                  <i className="fa-regular fa-code text-xxs group-hover:text-cosmic+2" />
                  View raw
                </TertiaryButton>
              )}
            </>
          )}
          <TertiaryButton
            className={clsx(
              "m-0 p-0 leading-3 bg-v2-primary flex items-center gap-1",
              "group hover:text-cosmic+2",
            )}
            onClick={onSwitchToSimpleSearch}
          >
            <i className="fa-regular fa-magnifying-glass text-xxs group-hover:text-cosmic+2" />
            Simple search
          </TertiaryButton>
        </div>
      </div>
      <div>
        {isRaw ? (
          <JsonEditor
            value={matcher}
            onChange={debouncedOnChangeRaw}
            mode="code"
            ace={ace}
            htmlElementProps={{ style: { height: 500 } }}
          />
        ) : (
          <div
            className={clsx(
              "react-awesome-query-builder",
              queryBuilderClassName,
            )}
          >
            <Query
              {...getAdvancedSearchEditorConfig(breakdowns)}
              value={tree}
              onChange={(val) => {
                setTree(val);
                debouncedOnChangeTree(val);
              }}
              renderBuilder={renderBuilder}
            />
          </div>
        )}
      </div>
    </div>
  );
};
